import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone } from 'lucide-react';
import { useEffect } from 'react';
import { FILL_GRADIENT } from '../../constants/gradients';

interface MobileWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * MobileWarningModal Component
 * 
 * Displays a warning to users accessing the studio on mobile/small screens,
 * recommending they use a desktop or larger screen for the best experience.
 */
const MobileWarningModal = ({ isOpen, onClose }: MobileWarningModalProps) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Auto-dismiss when screen size changes from mobile to desktop
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      // If screen becomes desktop size (≥1024px), dismiss the modal
      if (window.innerWidth >= 1024) {
        onClose();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, onClose]);

  const handleContinue = () => {
    // Store dismissal in localStorage
    localStorage.setItem('beemi_mobile_warning_dismissed', 'true');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-center justify-center p-4"
            style={{ 
              willChange: 'opacity',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100dvh', // Dynamic viewport height for mobile
            }}
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              style={{ 
                willChange: 'opacity, transform',
                position: 'relative',
                margin: 'auto',
                maxHeight: 'calc(100dvh - 2rem)', // Dynamic viewport height
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Gradient Line at Top */}
              <div 
                className="h-1 w-full flex-shrink-0" 
                style={{ background: FILL_GRADIENT }}
              />

              {/* Header with Icons */}
              <div className="p-8 text-center overflow-y-auto flex-1">
                <div className="flex items-center justify-center gap-4 mb-6">
                  {/* Mobile Icon */}
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center">
                    <Smartphone className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                  </div>
                  
                  {/* Arrow */}
                  <motion.div
                    initial={{ x: -5 }}
                    animate={{ x: 5 }}
                    transition={{ 
                      duration: 0.8,
                      repeat: Infinity,
                      repeatType: 'reverse',
                      ease: 'easeInOut'
                    }}
                  >
                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </motion.div>
                  
                  {/* Desktop Icon */}
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-yellow-100 dark:from-pink-900/30 dark:to-yellow-900/30 rounded-2xl flex items-center justify-center">
                    <Monitor className="w-8 h-8 text-pink-600 dark:text-pink-400" />
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-3">
                  Best Viewed on Desktop
                </h2>
                
                <p className="text-gray-600 dark:text-dark-text-muted leading-relaxed mb-4">
                  Beemi Studio is optimized for desktop and larger screens for the best experience.
                </p>
                
                <div className="bg-gradient-to-br from-orange-50 to-pink-50 dark:from-orange-900/10 dark:to-pink-900/10 rounded-xl p-4 border border-orange-100 dark:border-orange-900/20">
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Visit on your desktop:
                  </p>
                  <div className="inline-block px-3 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-base font-mono font-semibold text-gray-900 dark:text-gray-100">
                      studio.beemi.app
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-3 p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                <button
                  onClick={handleContinue}
                  className="w-full px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all hover:shadow-lg hover:scale-105 active:scale-95"
                  style={{ background: FILL_GRADIENT }}
                >
                  Continue on Mobile Anyway
                </button>
                
                <p className="text-xs text-center text-gray-500 dark:text-dark-text-muted">
                  You won't see this message again
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileWarningModal;
